import type { JSX } from 'react';
import { formatDateInfo } from '@/features/experiences/types/experience.types';
import type { Experience } from '@/features/experiences/types/experience.types';
import { DELETE_LOCKED_REASON } from '../utils/experience-lock';
import { ExperienceCardActions } from './ExperienceCardActions';
import { ExperienceStatusBadge } from './ExperienceStatusBadge';

interface ExperienceCardHeaderProps {
  experience: Experience;
  onEdit: (exp: Experience) => void;
  onDelete: (id: string) => void;
  editDisabledReason: string | undefined;
  isLocked: boolean;
}

function ExperienceSummary({ experience }: { experience: Experience }): JSX.Element {
  return (
    <div className="min-w-0">
      <p className="font-medium">{experience.jobTitle}</p>
      <p>{experience.companyName}</p>
      <p className="text-sm text-muted-foreground">
        {formatDateInfo(experience.startDate)} -{' '}
        {experience.isOngoing ? 'Present' : formatDateInfo(experience.endDate)}
      </p>
      <p className="text-sm text-muted-foreground">
        {experience.isFullTime ? 'Full-time' : 'Part-time'}
        {experience.isContractor && ' • Contractor'}
      </p>
    </div>
  );
}

export function ExperienceCardHeader({
  experience,
  onEdit,
  onDelete,
  editDisabledReason,
  isLocked,
}: ExperienceCardHeaderProps): JSX.Element {
  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
      <ExperienceSummary experience={experience} />
      <div
        data-testid="experience-card-header-end"
        className="flex items-start justify-between gap-2 sm:justify-end"
      >
        <ExperienceStatusBadge status={experience.verificationStatus} />
        <ExperienceCardActions
          onEdit={(): void => onEdit(experience)}
          onDelete={(): void => onDelete(experience.id)}
          isEditable={!editDisabledReason}
          editDisabledReason={editDisabledReason}
          isDeletable={!isLocked}
          deleteDisabledReason={isLocked ? DELETE_LOCKED_REASON : undefined}
        />
      </div>
    </div>
  );
}
